import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasRoles } from '../utils/roleUtils';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  
  // While checking authentication state, we can show a loading indicator
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  
  // If not authenticated, redirect to login with the current location in state
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // If admin access is required but user is not an admin, redirect to appointments (which all users can access)
  if (requireAdmin && !hasRoles(currentUser, ['admin', 'super_admin'])) {
    return <Navigate to="/crm/appointments" replace />;
  }
  
  // If authenticated and passes role check, render the children
  return <>{children}</>;
};

export default ProtectedRoute;